import { api, writeDB } from './store';
import { apollo } from './graphql';
import { ORDERS_PER_MINUTE, ORDERS_CREATED_PER_MINUTE, TOP_IPS, TOP_PAYMENT_FINGERPRINTS } from './queries/server';
import { parseServerTimeSeries } from './statistics';

/* INTERFACES */
export interface Queue {
  maximumSpotsCount: number;
  maximumCatalogSpotsCount: number;
  totalActiveUsersCount: number;
  waitingUsersCount: number;
}

export interface QueueStat {
  [key: number]: number;
}

/* METHODS */
export async function refreshQueueData(): Promise<void> {
  const response = await api.showWaitingQueue();
  writeDB.set('queue', response.data as Queue);
}

export async function setQueueLimit(data: {
  maximumSpotsCount?: number;
  maximumCatalogSpotsCount?: number;
}): Promise<void> {
  const queue = writeDB.get('queue');
  if (queue) {
    writeDB.set('queue', { ...queue, ...data });
  }
  await api.updateWaitingQueue(data);
}

export function pollQueueData(): { stop: () => void } {
  refreshQueueData();
  const interval = setInterval(refreshQueueData, 5000);
  return {
    stop: () => clearTimeout(interval),
  };
}

export async function showActiveSessionsAge(): Promise<void> {
  const result = await api.showSessionsStatsAgeActive();
  writeDB.set('queueActiveSessionsAge', result.data);
}

export async function showActiveSessionsWaitingTime(): Promise<void> {
  const result = await api.showSessionsStatsWaitingTime();
  writeDB.set('queueSessionWaitTime', result.data);
}

export async function showSessionsAge(): Promise<void> {
  const result = await api.showSessionsStatsAgeAll();
  writeDB.set('queueSessionsAge', result.data);
}

export async function loadOrdersPerMinute(): Promise<void> {
  const result = await apollo.query({
    query: ORDERS_PER_MINUTE,
    fetchPolicy: 'network-only',
  });
  writeDB.transaction((db, dispatch) => {
    dispatch(db.set('ordersPerMinute', parseServerTimeSeries(result.data.orderStatistics.ordersPerMinute)));
    dispatch(db.set('paymentsPerMinute', parseServerTimeSeries(result.data.orderStatistics.paymentsPerMinute)));
  });
}

export async function loadOrdersCreatedPerMinute(): Promise<void> {
  const from = new Date();
  const to = new Date(from);
  from.setMinutes(from.getMinutes() - 20);

  const result = await apollo.query({
    query: ORDERS_CREATED_PER_MINUTE,
    variables: {
      during: {
        from: from,
        to: to,
      },
    },
    fetchPolicy: 'network-only',
  });
  writeDB.transaction((db, dispatch) => {
    dispatch(
      db.set('ordersCreatedPerMinute', parseServerTimeSeries(result.data.orderStatistics.ordersCreatedPerMinute))
    );
  });
}

export async function loadTopIps(): Promise<void> {
  const from = new Date();
  const to = new Date(from);
  from.setMinutes(from.getMinutes() - 120);

  const result = await apollo.query({
    query: TOP_IPS,
    variables: {
      during: {
        from: from,
        to: to,
      },
    },
    fetchPolicy: 'network-only',
  });

  writeDB.transaction((db, dispatch) => {
    dispatch(db.set('topIps', result.data.orderStatistics.topIps));
  });
}

export async function loadTopPaymentFingerprints(): Promise<void> {
  const from = new Date();
  const to = new Date(from);
  from.setMinutes(from.getMinutes() - 30);

  const result = await apollo.query({
    query: TOP_PAYMENT_FINGERPRINTS,
    variables: {
      during: {
        from: from,
        to: to,
      },
    },
    fetchPolicy: 'network-only',
  });

  writeDB.transaction((db, dispatch) => {
    dispatch(db.set('topPaymentFingerprints', result.data.orderStatistics.topPaymentFingerprints));
  });
}
